import { memo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { NOT_APPLICABLE_STRING } from '../../../constants/general';
import { Icons, IconType } from '../../../constants/icons';
import { Images } from '../../../constants/images';
import { getPostVisual } from '../../../constants/Post/postBackground';
import { SVGIconType } from '../../../constants/svgIcon';
import { TranslationLocale } from '../../../i18n';
import { TtsLocale } from '../../../models/Base';
import {
  Audience,
  Channel,
  ChannelType,
  Post,
  PostContentType,
} from '../../../models/PostStore/Post';
import { Space, SpaceMembership } from '../../../models/SpaceStore';
import { Album } from '../../../models/SpaceStore/Album';
import { MultiplayerGame } from '../../../models/SpaceStore/MultiplayerGame';
import { Profile, ProfileMapType } from '../../../models/UserStore';
import { usePostStore } from '../../../stores/PostStore';
import { useSpaceStore } from '../../../stores/SpaceStore';
import { useUserStore } from '../../../stores/UserStore';
import { timeAgo } from '../../../utils/date';
import { getUiLocale } from '../../../utils/i18n';
import { ensureProtocol, isUrl } from '../../../utils/string';
import { AlbumViewCard } from '../AlbumViewCard';
import { AvatarNode } from '../AvatarNode';
import { GameCard } from '../GameCard';
import { ImagePuzzle } from '../ImagePuzzle';
import { LinkPreviewCard } from '../LinkPreviewCard';
import { MultiplayerGameCard } from '../MultiplayerGameCard';
import { SeparatorComponent } from '../SeparatorComponent';
import { Skeleton } from '../Skeleton';
import { SvgIcon } from '../SVGIcon';
import { TextJoyGroup } from '../TextJoyGroup';
import { VideoPlayer } from '../VideoPlayer';

import './PostView.css';

export type PostViewProps = {
  post: Post;
  creator: Profile | null | undefined;
  classroom: Space | null | undefined;
  //
  album: Album | null | undefined;
  albumClassroom: Space | null | undefined;
  membership: SpaceMembership | null | undefined;

  sharingSpace: Space | null | undefined;
  sharingSpaceOwner: Profile | null | undefined;

  sharingUser: Profile | null | undefined;

  sharingMultiplayerGame: MultiplayerGame | null | undefined;

  backgroundColor?: string;
};

type PostHeaderProps = {
  channel: Channel;
  creator: PostViewProps['creator'];

  postAudience: Audience | string[] | undefined;
  postCreatedAt: Date;

  postContentType: PostContentType;

  autoPostType?: 'album' | 'chess' | undefined;
  backgroundColor?: string;
};

const PostHeader = memo((props: PostHeaderProps) => {
  const {
    channel,
    creator,

    // spaceIcon,
    postContentType,

    postAudience,
    postCreatedAt,
    autoPostType = undefined,
  } = props;

  const { i18n } = useTranslation();
  const [spaceIconAvailable, setSpaceIconAvailable] = useState(true);

  const isUserHomeClubChannel = channel.id == creator?.userId;

  const space = useSpaceStore.getState().getSpace(channel.id);
  const spaceI18nInfo = space?.i18nClassroomInfo;

  const isUserChannel =
    channel.type == ChannelType.User || isUserHomeClubChannel;

  return (
    <div className="post-view-header">
      {isUserChannel ? (
        creator ? (
          <AvatarNode profile={creator} size={56} />
        ) : null
      ) : space?.icon?.publicUrls?.small && spaceIconAvailable ? (
        <img
          src={space?.icon?.publicUrls?.small}
          style={{
            width: '64px',
            height: '64px',
            border: '1px solid #cccccc50',
            // borderRadius: '1rem 1rem 0.5rem 0.5rem',
            borderRadius: '4rem',
            aspectRatio: space?.icon?.aspectRatio,
          }}
          onError={() => setSpaceIconAvailable(false)}
        />
      ) : (
        <Skeleton width={'70px'} height={'70px'} borderRadius={'4rem'} />
      )}

      <div className="post-header-info">
        {autoPostType == 'album' ? (
          <>
            <Trans
              i18nKey="SUBJECT added a Journey."
              values={{
                SUBJECT: isUserHomeClubChannel
                  ? creator?.name
                  : spaceI18nInfo?.resources[spaceI18nInfo?.defaultLocale]
                      ?.name,
              }}
            >
              <span style={{ fontWeight: 'bold' }} />
            </Trans>
          </>
        ) : (
          <p
            className="font-bold"
            style={{
              wordBreak: 'break-word',
            }}
          >
            {isUserHomeClubChannel
              ? creator?.name
              : spaceI18nInfo?.resources[spaceI18nInfo?.defaultLocale]?.name}
          </p>
        )}

        <div
          className="flex flex-row items-center"
          style={{ gap: '0.25rem', flexWrap: 'wrap' }}
        >
          {/* {channel.type == ChannelType.Space &&
            postContentType != PostContentType.PostGame &&
            !isUserHomeClubChannel ? (
              creator ? (
                <AvatarNode
                  avatar={{
                    ...creator?.avatar,
                    userId: creator.userId,
                    source:
                      creator.profilePicture &&
                      creator.profilePicture.publicUrls?.small
                        ? {
                            uri: creator.profilePicture.publicUrls?.small,
                          }
                        : '',
                  }}
                  size={32}
                />
              ) : null
            ) : null} */}

          <SeparatorComponent
            separator={<p className="text-gray-600 text-sm">·</p>}
          >
            {/* {channel.type == ChannelType.Space &&
              postContentType != PostContentType.PostGame &&
              !isUserHomeClubChannel ? (
                <p
                  className="text-gray-600 text-sm"
                  style={{
                    wordBreak: 'break-all',
                    flexWrap: 'wrap',
                    maxLines: 1,
                  }}
                >
                  {creator?.name}
                </p>
              ) : null} */}

            {postContentType == PostContentType.PostGame ? (
              <SvgIcon
                name={SVGIconType.GamifiedPost}
                width={20}
                height={20}
                color="#808080"
              />
            ) : // <p className="text-gray-600 text-sm">{t('GAMIFIED Post')}</p>
            null}

            <p className="text-gray-600 text-sm">
              {timeAgo(
                postCreatedAt,
                getUiLocale(i18n.language as TranslationLocale),
              )}
            </p>

            <SvgIcon
              name={
                postAudience == Audience.Public
                  ? SVGIconType.Region
                  : postAudience == Audience.Members
                  ? SVGIconType.Member
                  : postAudience == Audience.Followers
                  ? SVGIconType.Friend
                  : SVGIconType.MembershipTier
              }
              width={20}
              height={20}
              color="#808080"
            />
          </SeparatorComponent>
        </div>
      </div>
    </div>
  );
});

type PostContentProps = {
  postContent: Post['content'];
  album: PostViewProps['album'];
  albumClassroom: PostViewProps['albumClassroom'];

  // sharingSpaceIcon: PostViewProps['sharingSpaceIcon'];
  // sharingSpaceType: PostViewProps['sharingSpaceType'];
  // sharingSpaceBanner: PostViewProps['sharingSpaceBanner'];
  // sharingSpaceName: PostViewProps['sharingSpaceName'];
  // sharingSpacePostCount: PostViewProps['sharingSpacePostCount'];
  // sharingSpaceFollowerCount: PostViewProps['sharingSpaceFollowerCount'];
  sharingSpaceOwner: PostViewProps['sharingSpaceOwner'];
  sharingSpace: PostViewProps['sharingSpace'];

  sharingUser: PostViewProps['sharingUser'];
  multiplayerGame: PostViewProps['sharingMultiplayerGame'];

  backgroundColor?: string;
};

const PostContent = memo((props: PostContentProps) => {
  const {
    postContent,
    album,
    albumClassroom,
    //
    // sharingSpaceBanner,
    // sharingSpaceName,
    // sharingSpacePostCount,
    // sharingSpaceFollowerCount,
    // sharingSpaceIcon,
    // sharingSpaceOwner,
    sharingSpace,
    //
    sharingUser,

    multiplayerGame,

    backgroundColor,
  } = props;
  const { t } = useTranslation();

  const isValidUrl =
    postContent.type == PostContentType.Text ? isUrl(postContent.text) : false;

  const imageSrc =
    (postContent.type == PostContentType.Normal ||
      postContent.type == PostContentType.PostGame) &&
    postContent.graphics
      ? postContent.graphics[0]?.image?.publicUrls?.medium
      : undefined;
  const fallbackImageSrc = Images.JOYAJOY_logo;
  const audioSrc =
    (postContent.type == PostContentType.Normal && postContent.audio) ||
    (postContent.type == PostContentType.Text && postContent.audio)
      ? postContent.audio.publicUrls?.source
      : undefined;

  const visual =
    postContent.type == PostContentType.Text
      ? getPostVisual(postContent.background)
      : undefined;

  //ImageJOY
  const postGameImageSrc =
    postContent.type == PostContentType.PostGame && postContent.graphics
      ? postContent.graphics[0]?.image?.publicUrls?.medium
      : undefined;
  const postGameImagePuzzlePieceOrder =
    postContent.type == PostContentType.PostGame && postContent.imageGames
      ? postContent.imageGames[0]?.puzzle
      : undefined;
  const postGameImagePuzzleSize =
    postContent.type == PostContentType.PostGame && postContent.imageGames
      ? postContent.imageGames[0]?.gameData.pieces
      : undefined;

  //TextJOY
  const postGameTextWords =
    postContent.type == PostContentType.PostGame && postContent.textGame
      ? postContent.textGame.gameData.answer.words
      : undefined;

  const locale =
    postContent.type === PostContentType.PostGame
      ? postContent.textGame?.gameData.answer.locale
      : undefined;

  const postGameTextJOYImageJOYPreviewImageSrc =
    postContent.type == PostContentType.PostGame && postContent.previews
      ? postContent.previews[0]?.image?.publicUrls?.medium
      : undefined;
  const game =
    postContent.type == PostContentType.Game
      ? useSpaceStore.getState().getGame(postContent.gameId)
      : undefined;
  const gameAlbum =
    postContent.type == PostContentType.Game
      ? useSpaceStore.getState().getAlbum(postContent.gameSetId)
      : undefined;
  const gameAlbumClassroom =
    postContent.type == PostContentType.Game
      ? useSpaceStore.getState().getSpace(postContent.classroomId)
      : undefined;

  const isPuzzleJOY = postGameImagePuzzlePieceOrder != undefined;

  switch (postContent.type) {
    case PostContentType.Text:
      return (
        <div
          style={{
            position: 'relative',
            textAlign: 'center',
          }}
        >
          <img
            src={visual?.filePrefix}
            draggable="false"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
          {isValidUrl ? (
            <a
              target="_blank"
              href={ensureProtocol(postContent.text)}
              className="text-3xl url-text text-content"
              style={{
                color: visual?.textColor,
              }}
              rel="noreferrer"
            >
              {postContent.text}
            </a>
          ) : (
            <p
              className="text-content text-3xl"
              style={{
                color: visual?.textColor,
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {postContent.text}
            </p>
          )}
          {audioSrc ? (
            <audio controls controlsList="nodownload">
              <source src={audioSrc} type={postContent.audio?.mimeType} />
              Your browser does not support the audio element.
            </audio>
          ) : null}
        </div>
      );
    case PostContentType.Normal:
      return (
        <>
          {postContent.link?.url ? (
            <div
              style={{
                width: 'inherit',
                padding: '0rem 0.5rem',
              }}
            >
              <LinkPreviewCard
                description={postContent.link.description}
                title={postContent.link.title}
                imageSources={postContent.link.image?.publicUrls?.small}
                url={postContent.link.url}
              />
            </div>
          ) : null}

          {postContent.graphics != undefined &&
          postContent.graphics[0]?.video ? (
            <VideoPlayer
              url={postContent.graphics[0]?.video.publicUrls?.['web-optimized']}
            />
          ) : null}

          {postContent.graphics != undefined &&
          postContent.graphics[0]?.image ? (
            <img
              src={imageSrc || fallbackImageSrc}
              style={{
                width: '100%',
                height: 'auto',
              }}
              onError={(e) => {
                if (e.currentTarget instanceof HTMLImageElement) {
                  if (e.currentTarget.src !== fallbackImageSrc) {
                    e.currentTarget.src = fallbackImageSrc;
                  }
                }
              }}
            />
          ) : null}
          {audioSrc ? (
            <audio controls controlsList="nodownload">
              <source src={audioSrc} type={postContent.audio?.mimeType} />
              Your browser does not support the audio element.
            </audio>
          ) : null}
          {postContent.text ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                padding: '0.5rem 1rem',
              }}
            >
              <p>{postContent.text}</p>
            </div>
          ) : null}
        </>
      );

    case PostContentType.Album:
      if (album != null) {
        return (
          <div className="flex flex-col justify-center items-center w-full">
            {albumClassroom ? (
              <AlbumViewCard
                album={album}
                albumClassroom={albumClassroom}
                backgroundColor={backgroundColor}
              />
            ) : null}

            <div style={{ padding: '0.5rem' }}>
              <p>{postContent.text}</p>
            </div>
          </div>
        );
      } else {
        // album not exist
        return (
          <div
            className="flex flex-col gap-1 bg-gray-150"
            style={{ padding: '1rem', margin: '1rem', borderRadius: '1rem' }}
          >
            <div className="flex items-center gap-1">
              <img
                className="size-icon-m"
                src={Icons[IconType.ReportProblem]}
              />
              <p className="text-md">
                {t('Sorry, this content isn’t available at the moment.')}
              </p>
            </div>

            <p className="text-gray-600 text-sm">
              {t(
                'When this happens, it’s usually because the owner only shared it with a small group of people, changed who can see or it’s been deleted.',
              )}
            </p>
          </div>
        );
      }
    case PostContentType.User:
      return (
        <>
          {sharingUser ? (
            <div
              className="flex flex-col gap-1"
              style={{
                padding: '1rem',
                // margin: '1rem',
                borderRadius: '1rem',
                width: '96%',
                backgroundColor: '#cccccc30',
              }}
            >
              <div
                className="flex gap-1 flex-col"
                style={{ alignItems: 'start' }}
              >
                <p className="text-md font-bold">{sharingUser?.name}</p>
                <p className="text-md">
                  {t('{count} followers', {
                    count: sharingUser?.statistics.followerCount,
                  })}
                </p>

                <div
                  className="flex justify-center items-center"
                  style={{ width: '100%', aspectRatio: 1 }}
                >
                  <AvatarNode profile={sharingUser} size={168} />
                </div>
              </div>
            </div>
          ) : null}
        </>
      );
    case PostContentType.Space:
      return (
        <>
          {sharingSpace ? (
            <div
              className="flex flex-col w-full"
              style={{ padding: '0.25rem', gap: '0.25rem' }}
            >
              {/* <JoyajoyImage
                resource={{
                  uri:
                    sharingSpace?.i18nClassroomInfo.resources[
                      sharingSpace?.i18nClassroomInfo.defaultLocale
                    ]?.carousel[0]?.image?.publicUrls?.medium ??
                    getDefaultBannerSource(postContent.classroomId) ??
                    '',
                }}
                className={'large-image'}
              /> */}
              <img
                src={sharingSpace.icon?.publicUrls?.small}
                style={{
                  borderRadius: '50%',
                  border: '1px #E5E5E5 solid',
                  margin: '0.5rem',
                }}
                className={'large-image'}
              />
              <hr />
              <div
                className="flex flex-row"
                style={{ padding: '0.5rem', gap: '0.5rem' }}
              >
                {/* <img
                  src={sharingSpace.icon?.publicUrls?.small}
                  style={{
                    width: '64px',
                    height: '64px',
                    border: '1px solid #cccccc50',
                    borderRadius: '1rem 1rem 0.5rem 0.5rem',
                  }}
                /> */}

                <div className="flex flex-col" style={{ gap: '0.25rem' }}>
                  <p className="text-md font-bold">
                    {
                      sharingSpace?.i18nClassroomInfo.resources[
                        sharingSpace?.i18nClassroomInfo.defaultLocale
                      ]?.name
                    }
                  </p>
                  <p className="text-gray-600">
                    {t('{posts} posts・{followers} followers', {
                      posts:
                        sharingSpace.statistics.classroomChannelPublicPostCount,
                      followers: sharingSpace.followerTotal,
                    })}
                  </p>
                  {/* {sharingSpaceOwner ? (
                    <>
                      <div
                        className="flex flex-row items-center"
                        style={{ gap: '0.25rem' }}
                      >
                        {sharingSpaceOwner.avatar ? (
                          <AvatarNode
                            avatar={{
                              ...sharingSpaceOwner.avatar,
                              userId: sharingSpaceOwner.userId,
                              source:
                                sharingSpaceOwner.profilePicture &&
                                sharingSpaceOwner.profilePicture.publicUrls
                                  ?.small
                                  ? {
                                      uri: sharingSpaceOwner.profilePicture
                                        .publicUrls?.small,
                                    }
                                  : '',
                            }}
                            size={28}
                          />
                        ) : null}

                        <p className="text-gray-600">
                          {sharingSpaceOwner.name}
                        </p>
                        {sharingSpaceOwner.isVerified && (
                          <img
                            src={Icons[IconType.Certified]}
                            className="size-icon-s"
                          />
                        )}
                      </div>
                    </>
                  ) : null} */}
                </div>
              </div>
            </div>
          ) : null}
        </>
      );
    case PostContentType.Game:
      return (
        <>
          {gameAlbum && gameAlbumClassroom && game ? (
            <GameCard
              album={gameAlbum}
              albumClassroom={gameAlbumClassroom}
              game={game}
              backgroundColor={backgroundColor}
            />
          ) : null}
        </>
      );
    case PostContentType.Post:
      return (
        <>
          {postContent.text ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                padding: '0rem 1rem',
              }}
            >
              <p>{postContent.text}</p>
            </div>
          ) : null}
        </>
      );
    case PostContentType.PostGame:
      return (
        <>
          {postContent.graphics != undefined &&
          postContent.graphics[0]?.video &&
          !postGameTextJOYImageJOYPreviewImageSrc ? (
            <VideoPlayer
              url={postContent.graphics[0]?.video.publicUrls?.['web-optimized']}
            />
          ) : null}
          {!postGameTextJOYImageJOYPreviewImageSrc &&
          postGameImageSrc &&
          postGameImagePuzzlePieceOrder &&
          postGameImagePuzzleSize ? (
            <ImagePuzzle
              puzzleSize={postGameImagePuzzleSize}
              imageSrc={postGameImageSrc}
              pieceOrder={postGameImagePuzzlePieceOrder}
            />
          ) : null}

          {!postGameImagePuzzlePieceOrder &&
          postContent.graphics != undefined &&
          postContent.graphics[0]?.image ? (
            <img
              src={imageSrc || fallbackImageSrc}
              style={{
                width: '100%',
                height: 'auto',
              }}
              onError={(e) => {
                if (e.currentTarget instanceof HTMLImageElement) {
                  if (e.currentTarget.src !== fallbackImageSrc) {
                    e.currentTarget.src = fallbackImageSrc;
                  }
                }
              }}
            />
          ) : null}

          {postContent.link?.url ? (
            <div
              style={{
                width: 'inherit',
                padding: '0rem 0.5rem',
              }}
            >
              <LinkPreviewCard
                description={postContent.link.description}
                title={postContent.link.title}
                imageSources={postContent.link.image?.publicUrls?.small}
                url={postContent.link.url}
              />
            </div>
          ) : null}

          {postGameTextJOYImageJOYPreviewImageSrc ? (
            <div style={{ position: 'relative' }}>
              <img
                src={postGameTextJOYImageJOYPreviewImageSrc}
                style={{
                  width: '100%',
                  height: 'auto',
                }}
                onError={(e) => {
                  if (e.currentTarget instanceof HTMLImageElement) {
                    if (e.currentTarget.src !== fallbackImageSrc) {
                      e.currentTarget.src = fallbackImageSrc;
                    }
                  }
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#00000090',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SvgIcon
                  name={isPuzzleJOY ? 'PuzzleJOY' : 'TextJOY'}
                  width={40}
                  height={40}
                  color="white"
                />
              </div>
            </div>
          ) : postGameTextWords ? (
            postGameTextWords.map((item, index) => (
              <TextJoyGroup
                type={undefined}
                key={index}
                units={item.units}
                locale={locale ?? TtsLocale.US}
              />
            ))
          ) : null}

          {/* {!postGameTextJOYImageJOYPreviewImageSrc &&
          !postGameTextWords &&
          postContent.text ? (
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                padding: '0.25rem 1rem',
              }}
            >
              <p>{postContent.text}</p>
            </div>
          ) : null} */}
        </>
      );
    case PostContentType.MultiplayerGameSession:
      return <></>;
    case PostContentType.MultiplayerGame:
      return (
        <>
          {multiplayerGame ? (
            <MultiplayerGameCard
              multiplayerGame={multiplayerGame}
              backgroundColor={backgroundColor}
            />
          ) : null}
        </>
      );
    default:
      return <></>;
  }
});

const PostFooter = memo(() => {
  return <></>;
});

export const PostView = memo((props: PostViewProps) => {
  const {
    post,
    creator,

    album,
    albumClassroom,
    // membership,

    sharingSpaceOwner,
    sharingSpace,

    sharingUser,
    sharingMultiplayerGame,

    backgroundColor,
  } = props;

  // post visibility
  const postAudience = post.audience;
  // const isPostAvailable = post?.state == PostState.Available;
  // const isPublic = postAudience == Audience.Public;
  // const isMemberOnly =
  //   post?.audience == Audience.Members &&
  //   membership?.state == SpaceMembershipState.InGroup;
  // const isTierContent =
  //   Array.isArray(post?.audience) &&
  //   membership?.tier?.tierId &&
  //   post?.audience.includes(membership.tier.tierId);
  // const isRootAdmin =
  //   membership?.role !== undefined &&
  //   [SpaceMembershipRole.Admin, SpaceMembershipRole.Root].includes(
  //     membership.role,
  //   );

  // const allowDisplay =
  //   isPostAvailable &&
  //   (isPublic || isMemberOnly || isTierContent || isRootAdmin);

  //nestedPost
  const isNestedPost = post.content.type == PostContentType.Post;
  const nestedPostId =
    post.content.type == PostContentType.Post && 'postId' in post.content
      ? post.content.postId
      : NOT_APPLICABLE_STRING;
  const nestedPost = usePostStore.getState().getPost(nestedPostId);

  const nestedPostCreatorProfile = useUserStore
    .getState()
    .getProfile(nestedPost?.createdBy ?? NOT_APPLICABLE_STRING);
  const nestedSharingAlbumId =
    nestedPost &&
    nestedPost.content.type == PostContentType.Album &&
    'gameSetId' in nestedPost.content
      ? nestedPost?.content.gameSetId
      : NOT_APPLICABLE_STRING;

  const nestedSharingAlbumClassroomId =
    nestedPost &&
    nestedPost.content.type == PostContentType.Album &&
    'gameSetId' in nestedPost.content
      ? nestedPost?.content.classroomId
      : NOT_APPLICABLE_STRING;
  const subSharingAlbum = useSpaceStore
    .getState()
    .getAlbum(nestedSharingAlbumId);
  const subSharingAlbumlassroom = useSpaceStore
    .getState()
    .getSpace(nestedSharingAlbumClassroomId);

  const subSharingUserId =
    nestedPost?.content.type == PostContentType.User
      ? nestedPost.content.userId
      : NOT_APPLICABLE_STRING;
  const nestedSharingProfile = useUserStore
    .getState()
    .getProfile(subSharingUserId);

  const subSharingClassroomId =
    nestedPost?.content.type == PostContentType.Space
      ? nestedPost.content.classroomId
      : NOT_APPLICABLE_STRING;
  const nestedSharingClassroom = useSpaceStore
    .getState()
    .getSpace(subSharingClassroomId);

  const nestedSharingClassroomOwner = useUserStore
    .getState()
    .getProfileFromMap(ProfileMapType.SpaceRoot, subSharingClassroomId);
  const nestedSharingMultiplayerGame = useSpaceStore
    .getState()
    .getMultiplayerGame(
      post?.content.type == PostContentType.MultiplayerGame
        ? post.content.multiplayerGameId
        : NOT_APPLICABLE_STRING,
    );

  return (
    <>
      <div
        className="post-view unselectable"
        style={{ backgroundColor: backgroundColor || '#f2f2f2' }}
      >
        <PostHeader
          channel={post.channel}
          creator={creator}
          //
          //
          postContentType={post.content.type}
          //
          postAudience={postAudience}
          postCreatedAt={post.createdAt}
          autoPostType={
            album?.createdAt == post.createdAt ? 'album' : undefined
          }
        />
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {isNestedPost && nestedPost ? (
            <div
              style={{
                width: '96%',
                borderRadius: '0.5rem',
                border: '1px #cccccc solid',
                overflow: 'hidden',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <PostHeader
                channel={nestedPost.channel}
                creator={nestedPostCreatorProfile}
                //
                //
                postContentType={nestedPost.content.type}
                //
                postAudience={nestedPost.audience}
                postCreatedAt={nestedPost.createdAt}
              />
              <PostContent
                postContent={nestedPost.content}
                album={subSharingAlbum}
                albumClassroom={subSharingAlbumlassroom}
                //
                sharingSpace={nestedSharingClassroom}
                sharingSpaceOwner={nestedSharingClassroomOwner}
                //
                sharingUser={nestedSharingProfile}
                multiplayerGame={nestedSharingMultiplayerGame}
                backgroundColor={backgroundColor}
              />
            </div>
          ) : null}
          <PostContent
            postContent={post.content}
            album={album}
            albumClassroom={albumClassroom}
            //
            sharingSpace={sharingSpace}
            sharingSpaceOwner={sharingSpaceOwner}
            //
            sharingUser={sharingUser}
            multiplayerGame={sharingMultiplayerGame}
            backgroundColor={backgroundColor}
          />
        </div>
        <PostFooter />
      </div>
    </>
  );
});
